import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Navigation, ChevronDown, Search, X } from 'lucide-react';
import { POPULAR_CITIES, CITY_REGIONS } from '@/utils/cities';
import { pickLang, RTL_LANGS, COMMON, tt } from '@/utils/localizedStrings';
import { reverseGeocode } from '@/utils/reverseGeocode';

const CitySelector = ({ selectedCity, onSelect, onUseGPS, textColors, gpsLocation, gpsResolved }) => {
  const { t, i18n } = useTranslation();
  const lang = pickLang(i18n.language);
  const isRTL = RTL_LANGS.has(lang);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [geocodedLabel, setGeocodedLabel] = useState(null);
  const dropdownRef = useRef(null);

  // Reverse-geocode the GPS coords whenever they change AND no city is manually selected.
  // This gives the user a readable "City, Country" label so they don't think
  // the app is using a generic / nearest-big-city fallback.
  // IMPORTANT: only do this when gpsResolved=true (browser actually returned a
  // real position). If the user denied permission, gpsResolved=false and we
  // must NOT advertise the silent Mecca fallback as the user's real location.
  useEffect(() => {
    if (selectedCity || !gpsResolved || !gpsLocation?.lat || !gpsLocation?.lon) {
      setGeocodedLabel(null);
      return;
    }
    let cancelled = false;
    reverseGeocode(gpsLocation.lat, gpsLocation.lon, lang).then(res => {
      if (cancelled) return;
      if (res && (res.city || res.country)) {
        const parts = [res.city, res.country].filter(Boolean);
        setGeocodedLabel(parts.join(', '));
      }
    });
    return () => { cancelled = true; };
  }, [selectedCity, gpsResolved, gpsLocation?.lat, gpsLocation?.lon, lang]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Track viewport width for mobile vs desktop dropdown positioning
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const update = (e) => setIsMobile(e.matches ?? mq.matches);
    update(mq);
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Group cities by region for organized display, OR show filtered flat list when searching
  const grouped = useMemo(() => {
    if (query) {
      // Normalize Arabic: strip diacritics (تشكيل) and unify alef/yaa/taa forms
      const normalizeArabic = (s) => s
        .replace(/[\u064B-\u065F\u0670]/g, '')  // remove harakat (fatha, kasra, damma, shadda, etc.)
        .replace(/[\u0622\u0623\u0625]/g, '\u0627')  // unify alef variants (أ إ آ → ا)
        .replace(/\u0649/g, '\u064A')  // alef maqsura → yaa
        .replace(/\u0629/g, '\u0647'); // taa marbuta → haa
      const qLower = query.toLowerCase();
      const qNorm = normalizeArabic(query);
      return [{
        id: 'search',
        name_en: 'Results',
        name_ar: 'النتائج',
        cities: POPULAR_CITIES.filter(c =>
          c.name_en.toLowerCase().includes(qLower) ||
          c.country_en.toLowerCase().includes(qLower) ||
          normalizeArabic(c.name_ar).includes(qNorm) ||
          normalizeArabic(c.country_ar).includes(qNorm)
        )
      }];
    }
    return CITY_REGIONS.map(region => ({
      ...region,
      cities: region.ids.map(id => POPULAR_CITIES.find(c => c.id === id)).filter(Boolean)
    }));
  }, [query]);

  // Display label priority:
  //   1. User has explicitly picked a city from the list  → city name
  //   2. GPS is RESOLVED + reverse-geocoded a real city   → "City, Country"
  //   3. GPS is RESOLVED but geocoding hasn't returned yet → "My Location"
  //   4. GPS denied / not resolved                        → "Select city"
  const gpsActive = !selectedCity && gpsResolved === true && !!gpsLocation?.lat;
  const myLocationLabel = {
    en: 'My Location', ar: 'موقعي الحالي', id: 'Lokasi Saya', ur: 'میرا مقام',
    fr: 'Ma position', bn: 'আমার অবস্থান', zh: '我的位置',
    fa: 'موقعیت من', tr: 'Konumum',
  };
  const displayName = selectedCity
    ? (isRTL ? selectedCity.name_ar : selectedCity.name_en)
    : gpsActive
      ? (geocodedLabel || (myLocationLabel[lang] || myLocationLabel.en))
      : tt(COMMON.selectCity, lang);

  const totalResults = grouped.reduce((sum, r) => sum + r.cities.length, 0);

  return (
    <div className="relative" ref={dropdownRef} data-testid="city-selector">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg backdrop-blur-xl border transition-all hover:-translate-y-0.5"
        style={{
          background: 'rgba(255, 255, 255, 0.2)',
          borderColor: 'rgba(255, 255, 255, 0.3)',
          color: textColors.primary
        }}
        data-testid="city-selector-button"
        title={gpsActive ? (lang === 'ar' ? 'يتم استخدام موقعك الحقيقي بدقة' : 'Your exact GPS coordinates are being used') : undefined}
      >
        {gpsActive ? (
          <Navigation size={14} style={{ color: '#10B981' }} data-testid="gps-active-indicator" />
        ) : (
          <MapPin size={16} />
        )}
        <span className="font-medium text-sm max-w-[180px] truncate">{displayName}</span>
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <>
          {/* Mobile backdrop - tap-anywhere-to-close (only visible on small screens) */}
          <div
            className="sm:hidden fixed inset-0 z-40"
            style={{ background: 'rgba(0,0,0,0.35)' }}
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />

          <div
            className="
              z-50 flex flex-col overflow-hidden rounded-2xl border max-h-[80vh]
              sm:absolute sm:top-full sm:mt-2 sm:max-h-[520px]
              fixed sm:static
            "
            style={{
              // === Responsive sizing ===
              // Mobile: full viewport width with small margin, centered
              // Desktop (sm+): fixed 320px width, anchored to button
              width: 'min(360px, calc(100vw - 16px))',

              // === Positioning ===
              // Mobile (fixed): centered horizontally just below the header
              // Desktop (absolute, set via Tailwind sm:absolute): anchored to right of button (works for both LTR and RTL since button sits at the start of the header in both)
              top: '70px',
              left: '50%',
              transform: 'translateX(-50%)',

              // Visual
              background: '#0B132B',
              borderColor: 'rgba(255, 255, 255, 0.25)',
              boxShadow: '0 24px 64px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(255,255,255,0.08) inset',
            }}
            dir={isRTL ? 'rtl' : 'ltr'}
            data-testid="city-dropdown"
          >
          {/* GPS option */}
          <button
            onClick={() => { onUseGPS(); setOpen(false); }}
            className="w-full flex items-start gap-3 px-4 py-3 transition-colors hover:bg-white/10 border-b flex-shrink-0 text-start"
            style={{
              color: '#FFFFFF',
              borderColor: 'rgba(255,255,255,0.1)',
              background: gpsActive ? 'rgba(16, 185, 129, 0.10)' : 'transparent',
            }}
            data-testid="use-gps-button"
          >
            <Navigation
              size={16}
              style={{ color: gpsActive ? '#10B981' : '#60A5FA', marginTop: 2, flexShrink: 0 }}
            />
            <span className="flex flex-col gap-1 min-w-0 flex-1">
              <span className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                <span>{t('useMyLocation')}</span>
                {gpsActive && (
                  <span
                    className="text-[10px] font-bold px-1.5 py-0.5 rounded"
                    style={{ background: '#10B981', color: '#FFFFFF' }}
                    data-testid="gps-active-badge"
                  >
                    ● {lang === 'ar' ? 'مُفعّل' : lang === 'ur' ? 'فعال' : lang === 'fa' ? 'فعال' : lang === 'tr' ? 'AKTİF' : lang === 'zh' ? '已激活' : lang === 'fr' ? 'ACTIF' : lang === 'id' ? 'AKTIF' : lang === 'bn' ? 'সক্রিয়' : 'ACTIVE'}
                  </span>
                )}
              </span>
              {gpsActive && geocodedLabel && (
                <span
                  className="text-xs font-medium"
                  style={{ color: '#10B981' }}
                  data-testid="gps-current-place"
                >
                  📍 {geocodedLabel}
                </span>
              )}
              <span
                className="text-xs leading-relaxed"
                style={{ color: 'rgba(255,255,255,0.62)' }}
                data-testid="use-gps-hint"
              >
                {t('useMyLocationHint')}
              </span>
            </span>
          </button>

          {/* Search input */}
          <div
            className="p-2.5 border-b flex-shrink-0"
            style={{ background: '#0B132B', borderColor: 'rgba(255,255,255,0.1)' }}
          >
            <div className="relative">
              <Search size={14} className="absolute top-1/2 -translate-y-1/2"
                style={{ [isRTL ? 'right' : 'left']: 12, color: 'rgba(255,255,255,0.55)' }} />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={tt(COMMON.searchCity, lang)}
                className="w-full text-white px-10 py-2.5 rounded-lg text-sm outline-none border focus:border-blue-400/60"
                style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.1)' }}
                data-testid="city-search-input"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="absolute top-1/2 -translate-y-1/2"
                  style={{ [isRTL ? 'left' : 'right']: 10, color: 'rgba(255,255,255,0.6)' }}
                  data-testid="clear-search"
                  aria-label="clear"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            {query && (
              <div className="text-[10px] mt-1.5 px-1" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {totalResults} {totalResults === 1 ? tt(COMMON.result, lang) : tt(COMMON.results, lang)}
              </div>
            )}
          </div>

          {/* Cities list - scrollable, grouped by region */}
          <div
            className="overflow-y-auto flex-1 falak-scrollbar"
            style={{ minHeight: 0 }}
            data-testid="city-list"
          >
            {totalResults === 0 && (
              <div className="px-4 py-6 text-sm text-center" style={{ color: 'rgba(255,255,255,0.55)' }}>
                {tt(COMMON.noResults, lang)}
              </div>
            )}
            {grouped.map(region => (
              region.cities.length > 0 && (
                <div key={region.id}>
                  {!query && (
                    <div
                      className="px-4 py-1.5 text-[10px] font-bold uppercase tracking-wider sticky top-0 z-10"
                      style={{
                        color: '#94A3B8',
                        background: '#0B132B',
                        letterSpacing: '0.08em'
                      }}
                    >
                      {isRTL ? region.name_ar : region.name_en}
                    </div>
                  )}
                  {region.cities.map(city => (
                    <button
                      key={city.id}
                      onClick={() => {
                        onSelect(city);
                        setOpen(false);
                        setQuery('');
                      }}
                      className="w-full flex items-center justify-between px-4 py-2 transition-colors hover:bg-white/10"
                      style={{ color: '#FFFFFF' }}
                      data-testid={`city-option-${city.id}`}
                    >
                      <div className="text-start min-w-0 flex-1">
                        <div className="text-sm font-medium truncate" style={{ color: '#FFFFFF' }}>
                          {isRTL ? city.name_ar : city.name_en}
                        </div>
                        <div className="text-[11px] truncate" style={{ color: 'rgba(255,255,255,0.6)' }}>
                          {isRTL ? city.country_ar : city.country_en}
                        </div>
                      </div>
                      {selectedCity && selectedCity.id === city.id && (
                        <span className="text-base font-bold ms-2" style={{ color: '#60A5FA' }}>✓</span>
                      )}
                    </button>
                  ))}
                </div>
              )
            ))}
          </div>

          {/* Footer count */}
          {!query && (
            <div
              className="px-4 py-2 border-t text-[11px] text-center flex-shrink-0"
              style={{
                background: '#0B132B',
                borderColor: 'rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.5)'
              }}
            >
              {`${POPULAR_CITIES.length} ${tt(COMMON.citiesWorldwide, lang)}`}
            </div>
          )}
        </div>
        </>
      )}
    </div>
  );
};

export default CitySelector;
